import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMenu } from '../../../shared/hooks/useMenu.js';
import { VENDOR_ROUTE_REGISTRY } from './VendorRouteRegistry.js';
import type { MenuSection } from '../../../shared/hooks/useMenu.js';

const SECTION_LABELS: Record<string, string> = {
  OVERVIEW: 'Overview',
  PAINTER: 'Painters',
  QUALITY: 'Quality',
  BILLING: 'Billing',
};

function fallbackSections(): MenuSection[] {
  const map = new Map<string, { code: string; label: string; items: { id: string; code: string; label: string; path: string; icon: string; section: string; sortOrder: number; isVisible: boolean }[] }>();
  for (const r of VENDOR_ROUTE_REGISTRY) {
    const sec = map.get(r.section) ?? {
      code: r.section,
      label: SECTION_LABELS[r.section] ?? r.section,
      items: [],
    };
    sec.items.push({ id: r.path, code: r.path, label: r.label, path: r.path, icon: '', section: r.section, sortOrder: r.sortOrder, isVisible: true });
    map.set(r.section, sec);
  }
  return Array.from(map.values());
}

export interface VendorSidebarProps {
  readonly collapsed: boolean;
}

export function VendorSidebar({ collapsed }: VendorSidebarProps): React.ReactElement {
  const { menu, loading, error } = useMenu('/api', undefined, 'web');
  const loc = useLocation();
  const sections: MenuSection[] = menu && menu.sections.length > 0 ? [...menu.sections] : fallbackSections();

  return (
    <nav
      role="navigation"
      aria-label="sidebar"
      data-testid="vendor-sidebar"
      data-collapsed={collapsed}
      style={{
        gridArea: 'sidebar',
        width: collapsed ? 0 : 220,
        overflowY: 'auto',
        minHeight: 0,
        background: 'var(--color-bg-elevated)',
        borderRight: '1px solid var(--color-border-default)',
        transition: 'width var(--motion-duration-base, 200ms)',
      }}
    >
      {!collapsed && (
        <div style={{ padding: 8 }}>
          {loading && <div data-testid="vendor-sidebar-loading">Loading…</div>}
          {error && (
            <div data-testid="vendor-sidebar-error" role="alert" style={{ color: 'var(--color-warning)', fontSize: 'var(--font-size-xs)', padding: 4 }}>
              Offline menu
            </div>
          )}
          {sections.map((sec) => (
            <div key={sec.code} data-testid={`sidebar-section-${sec.code}`} style={{ marginBottom: 12 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  color: 'var(--color-text-muted)',
                  padding: '4px 8px',
                  letterSpacing: '0.05em',
                }}
              >
                {sec.label}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {sec.items.map((it) => {
                  const active = loc.pathname === it.path;
                  return (
                    <li key={it.id}>
                      <Link
                        to={it.path}
                        data-testid={`sidebar-item-${it.id}`}
                        style={{
                          display: 'block',
                          padding: '8px 12px',
                          color: active ? 'var(--color-brand-primary)' : 'var(--color-text-primary)',
                          background: active ? 'var(--color-info-bg)' : 'transparent',
                          textDecoration: 'none',
                        }}
                      >
                        {it.label}
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}
